import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Delete, LogIn, LogOut, ShieldCheck, XCircle, CalendarOff, History } from "lucide-react";
import { api, errMsg, fmtTime } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

const Clock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return (
    <div>
      <p data-testid="kiosk-clock" className="font-mono text-6xl sm:text-7xl lg:text-8xl font-bold tracking-tight tabular-nums">
        {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}
      </p>
      <p className="font-display text-lg text-slate-400 mt-2">
        {now.toLocaleDateString([], { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
      </p>
    </div>
  );
};

const ResultCard = ({ result, onClose }) => {
  const ok = !result.error;
  const r = result.data;
  const isOff = ok && r.record.weekly_off;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div data-testid="kiosk-attendance-result-modal" className={`pop w-full max-w-md rounded-2xl p-8 border-2 bg-slate-900 ${ok ? (isOff ? "border-indigo-500" : "border-emerald-500") : "border-rose-500"}`}>
        {ok ? (
          <>
            <div className="flex items-center gap-3">
              {r.action === "check_in" ? <LogIn className="text-emerald-400" size={28} /> : <LogOut className="text-sky-400" size={28} />}
              <span data-testid="kiosk-result-action" className="font-mono text-sm tracking-[0.25em] uppercase text-slate-300">
                {r.action === "check_in" ? "Checked In" : "Checked Out"}
              </span>
            </div>
            <h3 data-testid="kiosk-result-name" className="font-display text-4xl font-extrabold mt-4">{r.worker.name}</h3>
            <p className="text-slate-400 font-mono text-sm mt-1">{r.worker.code} · {r.worker.department || "—"}</p>
            <div className="grid grid-cols-2 gap-4 mt-6 font-mono text-sm">
              <div><p className="text-slate-500 text-xs uppercase">In</p><p className="text-lg">{fmtTime(r.record.check_in)}</p></div>
              <div><p className="text-slate-500 text-xs uppercase">Out</p><p className="text-lg">{fmtTime(r.record.check_out)}</p></div>
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <StatusBadge status={r.record.status} testId="kiosk-result-status" />
              {r.record.late_minutes > 0 && <span className="text-amber-300 text-sm font-mono">{r.record.late_minutes} min late</span>}
              {r.action === "check_out" && <span className="text-slate-300 text-sm font-mono">{r.record.hours} h worked</span>}
            </div>
            {isOff && (
              <p className="mt-4 flex items-center gap-2 text-indigo-300 text-sm"><CalendarOff size={16} /> {r.holiday ? `Today is a holiday (${r.holiday})` : "Today is your weekly off"} — attendance logged.</p>
            )}
          </>
        ) : (
          <div className="text-center">
            <XCircle className="mx-auto text-rose-400" size={48} />
            <p data-testid="kiosk-result-error" className="font-display text-2xl font-bold mt-4">{result.error}</p>
          </div>
        )}
        <p className="text-center text-xs text-slate-500 mt-8 font-mono">Tap anywhere to continue</p>
      </div>
    </div>
  );
};

const Recent = ({ items }) => (
  <div className="space-y-2" data-testid="kiosk-recent-list">
    <p className="font-mono text-xs tracking-[0.2em] text-emerald-400 uppercase flex items-center gap-2">
      <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" /></span>
      Live today
    </p>
    {items.length === 0 && <p className="text-slate-500 text-sm">No punches yet today.</p>}
    {items.map((i, idx) => (
      <div key={i.name + i.check_in} className="rise flex items-center justify-between gap-3 py-2 border-b border-slate-800/80" style={{ animationDelay: `${idx * 40}ms` }}>
        <span className="text-slate-200 truncate">{i.name}</span>
        <span className="flex items-center gap-3 font-mono text-xs text-slate-400 shrink-0">
          {fmtTime(i.check_out || i.check_in)} {i.check_out ? "OUT" : "IN"}
          <StatusBadge status={i.status} />
        </span>
      </div>
    ))}
  </div>
);

export default function Kiosk() {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [recent, setRecent] = useState([]);
  const [info, setInfo] = useState({ company_name: "" });
  const inputRef = useRef(null);

  const loadRecent = useCallback(() => api.get("/public/recent").then((r) => setRecent(r.data)).catch(() => {}), []);
  useEffect(() => {
    api.get("/public/info").then((r) => setInfo(r.data)).catch(() => {});
    loadRecent();
    const t = setInterval(loadRecent, 4000);
    return () => clearInterval(t);
  }, [loadRecent]);

  const close = useCallback(() => { setResult(null); setCode(""); setTimeout(() => inputRef.current?.focus(), 50); }, []);
  useEffect(() => { if (!result) return; const t = setTimeout(close, 3500); return () => clearTimeout(t); }, [result, close]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!code.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/punch", { code });
      setResult({ data });
      loadRecent();
    } catch (err) {
      setResult({ error: errMsg(err) });
    } finally {
      setBusy(false);
    }
  };

  const press = (k) => { setCode((c) => (c + k).slice(0, 20)); inputRef.current?.focus(); };

  return (
    <div className="min-h-screen bg-slate-950 text-white relative overflow-hidden">
      <div className="absolute inset-0 grid-bg pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 py-8 lg:py-12 grid lg:grid-cols-12 gap-10 min-h-screen">
        <section className="lg:col-span-7 flex flex-col gap-10 rise">
          <header className="flex items-center justify-between">
            <div>
              <p className="font-mono text-[10px] tracking-[0.3em] text-emerald-400 uppercase">Attendance Terminal</p>
              <h1 data-testid="kiosk-company-name" className="font-display text-2xl font-extrabold">{info.company_name}</h1>
            </div>
            <div className="flex gap-2">
            <Link to="/history" data-testid="kiosk-history-link" className="flex items-center gap-2 text-xs font-mono text-emerald-300 hover:text-white border border-emerald-800/60 hover:border-emerald-500 rounded-full px-4 py-2 transition-colors">
              <History size={14} /> My History
            </Link>
            <Link to="/admin" data-testid="kiosk-admin-link" className="flex items-center gap-2 text-xs font-mono text-slate-400 hover:text-white border border-slate-800 hover:border-slate-600 rounded-full px-4 py-2 transition-colors">
              <ShieldCheck size={14} /> Admin
            </Link>
            </div>
          </header>
          <Clock />
          <div className="flex-1 bg-slate-900/60 backdrop-blur-xl border border-slate-800 rounded-2xl p-6 max-h-[420px] overflow-y-auto">
            <Recent items={recent} />
          </div>
        </section>

        <section className="lg:col-span-5 flex items-center rise" style={{ animationDelay: "120ms" }}>
          <form onSubmit={submit} className="w-full bg-slate-900 border border-slate-800 rounded-2xl p-6 md:p-8 shadow-2xl">
            <p className="font-mono text-xs tracking-[0.2em] text-slate-400 uppercase">Enter your worker code</p>
            <input
              ref={inputRef}
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              data-testid="kiosk-worker-code-input"
              placeholder="••••"
              className="mt-3 w-full bg-slate-950 border-2 border-slate-800 focus:border-emerald-500 outline-none rounded-xl px-5 py-4 font-mono text-4xl tracking-[0.3em] text-center transition-colors"
            />
            <div className="grid grid-cols-3 gap-3 mt-6">
              {KEYS.map((k) => <Key key={k} k={k} onPress={press} />)}
              <button type="button" onClick={() => setCode("")} data-testid="kiosk-keypad-clear-btn" className="h-16 rounded-xl border border-slate-800 font-mono text-sm text-rose-300 hover:bg-rose-500/10 active:scale-[0.97] transition-colors">CLR</button>
              <Key k="0" onPress={press} />
              <button type="button" onClick={() => setCode((c) => c.slice(0, -1))} data-testid="kiosk-keypad-backspace-btn" className="h-16 rounded-xl border border-slate-800 grid place-items-center hover:bg-slate-800 active:scale-[0.97] transition-colors"><Delete size={20} /></button>
            </div>
            <button type="submit" disabled={busy || !code} data-testid="kiosk-checkin-submit-btn" className="mt-6 w-full h-16 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-950 font-display text-xl font-bold shadow-lg shadow-emerald-500/20 active:scale-[0.98] transition-colors">
              {busy ? "Recording…" : "Mark Attendance"}
            </button>
            <p className="text-center text-xs text-slate-500 mt-4 font-mono">Press Enter to submit · first punch = IN, second = OUT</p>
          </form>
        </section>
      </div>
      {result && <ResultCard result={result} onClose={close} />}
    </div>
  );
}

const Key = ({ k, onPress }) => (
  <button type="button" onClick={() => onPress(k)} data-testid={`kiosk-keypad-digit-${k}`} className="h-16 rounded-xl border border-slate-800 font-mono text-2xl font-bold hover:bg-slate-800 active:scale-[0.97] transition-colors">
    {k}
  </button>
);
